/**
 * useTutorApplicationVideoQuestionState — une question vidéo de la candidature (motivation, étape 3 ;
 * pédagogie, 5 ; technique, 6 et 7), de sa lecture à la fin d'étape. Port de l'ancien
 * `InterviewScreen` (la lecture, le champion) et d'`InterviewQuestion` (le reste), dans le même ordre :
 *
 *  1. à l'ouverture, `GET /api/v1/test-questions` (`step_type: video`, la catégorie, et le domaine
 *     `subject_type` d'une étape technique d'un double profil) ; le squelette pendant la lecture ;
 *  2. la question montrée, la vidéo d'avant oubliée (`setVideoRecord(null)`) et, si elle n'a pas
 *     encore de réponse, « vue » (`submit-view`) : la règle des 10 minutes part de là ;
 *  3. la réflexion décompte à la seconde ; à zéro, ou sur « Commencer » (qui la saute), la caméra
 *     s'ouvre (`video-recording`) ;
 *  4. la vidéo revenue part 700 ms plus tard (`useTutorApplicationVideoUploadAdapter`), la part
 *     envoyée à l'écran ;
 *  5. la vidéo rangée (ou la question passée, trop tard) : on la revoit, « Terminer » ouvre la fin
 *     d'étape (`getCongratsText`, le champion de la question).
 *
 * La croix confirme toujours (« … c'est comme si tu avais passé la question. »), et disparaît une
 * fois la réponse là ; elle est mesurée comme la croix de l'ancien `FormStepper`.
 */
import { useNavigation } from "@react-navigation/native"
import { useVideoPlayer, VideoView } from "expo-video"
import moment from "moment"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { apiRequest } from "../../../../../../data/redux/actions/api"
import { setVideoRecord } from "../../../../../../data/redux/actions/camera"
import { tutorsService } from "../../../../../../data/services/tutorsService"
import { type TutorStudentWorkPart } from "../../../../../../design-system/organisms/TutorStudentWorkViewer/TutorStudentWorkViewer"
import { type TutorApplicationVideoQuestionPageProps } from "../../../../../../design-system/pages/TutorApplicationVideoQuestionPage/TutorApplicationVideoQuestionPage"
import { useInterval } from "../../../../../../engine/shared/useInterval"
import { interviewStepDoneText } from "../../../../adapters/tutor/Application/tutorApplicationAdapter"
import {
    type ApiApplicationAnswer,
    type ApiApplicationQuestion,
    answerOf,
    hasVideo,
    initialReflectionSeconds,
    isTooLate,
    toUIVideoQuestion,
    videoQuestionChampion,
    videoQuestionPhase,
    videoQuestionsOf,
} from "../../../../adapters/tutor/Application/tutorVideoQuestionAdapter"
import { track } from "../../../hooks/trackEvent"
import {
    type ApplicationVideoRecord,
    useTutorApplicationVideoUploadAdapter,
} from "../../../hooks/useTutorApplicationVideoUploadAdapter"
import { ACTIVITY_SOUNDS } from "../../student/Activity/activitySounds"
import { playStudentSound } from "../../student/studentSounds"
import { applicationAsker, selectCandidate } from "./candidateState"
import { STEP_DONE_EVENT } from "./useTutorApplicationForm"
import { INTERVIEW_EXIT_MESSAGE } from "./useTutorApplicationWrittenState"

type VideoState = {
    data?: { tutors?: { testQuestions?: ApiApplicationQuestion[] | null; testAnswers?: ApiApplicationAnswer[] | null } }
    camera?: { videoRecord?: ApplicationVideoRecord }
    api?: Record<string, { request?: boolean } | undefined>
}

type Navigation = {
    push: (route: string, params?: object) => void
    goBack: () => void
}

/** Le temps laissé avant l'envoi, après le retour de la caméra (`InterviewQuestion`). */
export const UPLOAD_DELAY_MS = 700

export function useTutorApplicationVideoQuestionState(category: string, stepNum: number) {
    const dispatch = useDispatch()
    const navigation = useNavigation() as unknown as Navigation
    const user = useSelector(selectCandidate)
    const served = useSelector((state: VideoState) => state?.data?.tutors?.testQuestions ?? null)
    const answers = useSelector((state: VideoState) => state?.data?.tutors?.testAnswers ?? null)
    const videoRecord = useSelector((state: VideoState) => state?.camera?.videoRecord ?? null)
    const reading = useSelector((state: VideoState) => !!state?.api?.[tutorsService.getTestQuestions.name]?.request)

    // 1. La lecture des questions de l'étape, une fois.
    const [asked, setAsked] = useState(false)
    useEffect(() => {
        const params: Record<string, string> = { test_category: category, step_type: "video" }
        const subjectType = user?.application_steps?.[stepNum - 1]?.subject_type
        if (subjectType) params.subject_type = subjectType
        dispatch(apiRequest(tutorsService.getTestQuestions, {}, {}, params) as never)
        setAsked(true)
        // Une lecture à l'ouverture, comme l'ancien.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const loading = !asked || reading
    const question = useMemo(
        () => (loading ? undefined : videoQuestionsOf(served, category)[0]),
        [loading, served, category]
    )
    const questionId = question?.id ?? null
    const answer = answerOf(answers, questionId)
    const champion = videoQuestionChampion(served, category)
    // Mémorisées : le décompte re-rend la page à la seconde, et la copie (`React.memo`) n'a pas à le suivre.
    const uiQuestion = useMemo(() => toUIVideoQuestion(question), [question])
    const askedBy = useMemo(() => applicationAsker(user, stepNum, champion), [user, stepNum, champion])

    // 2. La question montrée : la vidéo d'avant oubliée, la vue envoyée, la mesure de l'étape.
    const [secondsLeft, setSecondsLeft] = useState<number | null>(null)
    useEffect(() => {
        if (questionId == null) return
        dispatch(setVideoRecord(null) as never)
        if (answer == null) dispatch(apiRequest(tutorsService.submitQuestionViewed, { id: questionId }) as never)
        setSecondsLeft(initialReflectionSeconds(question, answer))
        track({ name: "form.step.started", properties: { step: `${question?.category}_${questionId}` } })
        // La question une fois montrée ; sa réponse est celle de ce moment-là.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [questionId])

    // 3. La réflexion : à la seconde, tant qu'il n'y a ni vidéo ni question passée.
    const tooLate = answer != null && isTooLate(answer)
    const ticking = secondsLeft != null && secondsLeft > 0 && !answer?.video && !tooLate
    useInterval(() => setSecondsLeft((left) => (left == null ? left : left - 1)), ticking ? 1000 : null)

    const openCamera = useCallback(() => {
        navigation.push("video-recording", { questionId: String(questionId), testCategory: question?.category })
    }, [navigation, question?.category, questionId])

    useEffect(() => {
        if (secondsLeft === 0 && !answer?.video && questionId != null) openCamera()
        // L'ancien n'ouvrait la caméra qu'à l'arrivée à zéro.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [secondsLeft])

    const onStart = () => {
        if (answer?.video) return
        if ((secondsLeft ?? 0) > 0) setSecondsLeft(0)
        else openCamera()
    }

    // 4. La vidéo revenue part.
    const upload = useTutorApplicationVideoUploadAdapter({
        questionId,
        videoRecord,
        displayName: (user as { display_name?: string | null } | null)?.display_name,
    })
    const startUploadRef = useRef(upload.start)
    startUploadRef.current = upload.start
    useEffect(() => {
        const timer = setTimeout(() => {
            if (!answer?.video && videoRecord) startUploadRef.current()
        }, UPLOAD_DELAY_MS)
        return () => clearTimeout(timer)
        // L'envoi suit la vidéo posée par la caméra.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoRecord])

    // 5. La vidéo à revoir, puis la fin d'étape.
    const playerUri = answer?.answer?.startsWith("video_tmp:") ? videoRecord?.uri : answer?.video
    const player = useVideoPlayer(playerUri || null)
    const [done, setDone] = useState(false)
    useEffect(() => {
        if (!done) return
        track({ name: STEP_DONE_EVENT })
        return playStudentSound(ACTIVITY_SOUNDS.finished, false, "finished")
    }, [done])

    const [exitOpen, setExitOpen] = useState(false)
    const [workPart, setWorkPart] = useState<TutorStudentWorkPart>("copy")

    const phase = videoQuestionPhase({
        answer,
        uploading: upload.uploading,
        percent: upload.percent,
        secondsLeft: secondsLeft ?? Number(question?.reflection_duration ?? 0),
        now: moment(),
    })

    const page: TutorApplicationVideoQuestionPageProps = {
        question: uiQuestion,
        loading,
        askedBy,
        phase,
        alreadySeen: !loading && question == null,
        player: hasVideo(answer) ? (
            <VideoView player={player} style={{ flex: 1 }} nativeControls contentFit="contain" />
        ) : null,
        onStart,
        onFinish: () => {
            dispatch(setVideoRecord(null) as never)
            setDone(true)
        },
        onContinue: () => navigation.goBack(),
        onClose: () => {
            track({ name: "button.clicked:progress-stepper.exit" })
            setExitOpen(true)
        },
        workPart,
        onChangeWorkPart: setWorkPart,
        exitConfirm: {
            visible: exitOpen,
            message: INTERVIEW_EXIT_MESSAGE,
            onConfirm: () => {
                setExitOpen(false)
                navigation.goBack()
            },
            onCancel: () => setExitOpen(false),
        },
    }

    return {
        page,
        done,
        stepDone: {
            champion: champion ?? "sam",
            text: interviewStepDoneText(stepNum, user?.profile?.relevant_subjects),
            onNext: () => navigation.goBack(),
        },
    }
}
